import React from "react";
import { useCurrency } from "@/context/CurrencyContext";

const STATUS_LABEL = { pa_paguar: "Pa paguar", pjeserisht: "Pjesërisht", paguar: "Paguar" };

export default function InvoicePrint({ invoice, patient, onClose }) {
  const { format, settings } = useCurrency();
  if (!invoice) return null;
  const brand = settings?.clinic_name || "DentArt";
  const phone = settings?.clinic_phone || "";
  const address = settings?.clinic_address || "Klinikë Dentare";

  const print = () => window.print();

  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto print:bg-white print:static print:overflow-visible" data-testid="invoice-print-modal">
      <div className="min-h-screen flex items-start justify-center p-6 print:p-0 print:block">
        <div className="bg-white w-full max-w-3xl rounded-xl shadow-2xl print:shadow-none print:rounded-none">
          {/* Toolbar (hidden in print) */}
          <div className="flex items-center justify-between border-b border-[#E2DFD8] px-6 py-3 print:hidden">
            <div className="text-sm text-[#5C5C5C]">Parapamje · {invoice.invoice_number}</div>
            <div className="flex gap-2">
              <button onClick={print} className="btn-primary" data-testid="print-invoice-button">Printo / PDF</button>
              <button onClick={onClose} className="btn-ghost">Mbyll</button>
            </div>
          </div>

          {/* Printable */}
          <div className="p-10 print:p-12" id="invoice-print">
            <div className="flex items-start justify-between mb-10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C5C5C]">{brand}</div>
                <div className="text-2xl font-semibold tracking-tight mt-1">{brand}</div>
                <div className="text-sm text-[#5C5C5C] mt-2">{address}{phone ? ` · ${phone}` : ""}</div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-semibold tracking-tight">FATURË</div>
                <div className="mt-1 font-mono text-sm text-[#2C4C3B]">{invoice.invoice_number}</div>
                <div className="text-xs text-[#5C5C5C] mt-1">
                  {new Date(invoice.created_at).toLocaleDateString("sq-AL", { dateStyle: "long" })}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8 mb-10">
              <div>
                <div className="label-eyebrow">Faturuar Pacientit</div>
                <div className="mt-2 font-semibold text-lg">
                  {patient ? `${patient.first_name} ${patient.last_name}` : "—"}
                </div>
                {patient && (
                  <div className="text-sm text-[#5C5C5C]">
                    {patient.phone || ""}<br/>
                    {patient.email || ""}<br/>
                    {patient.address || ""}
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="label-eyebrow">Statusi</div>
                <div className="mt-2 inline-block rounded-full bg-[#EAE4D9] px-4 py-1 text-sm font-medium text-[#2C4C3B]">
                  {STATUS_LABEL[invoice.status]}
                </div>
              </div>
            </div>

            <table className="w-full text-sm mb-8">
              <thead>
                <tr className="border-b-2 border-[#1A1A1A]">
                  <th className="py-3 text-left label-eyebrow">Përshkrimi</th>
                  <th className="py-3 text-right label-eyebrow">Sasia</th>
                  <th className="py-3 text-right label-eyebrow">Çmimi</th>
                  <th className="py-3 text-right label-eyebrow">Totali</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.items || []).map((it, i) => (
                  <tr key={i} className="border-b border-[#E2DFD8]">
                    <td className="py-3">{it.description}</td>
                    <td className="py-3 text-right">{it.quantity}</td>
                    <td className="py-3 text-right font-mono">{format(it.unit_price)}</td>
                    <td className="py-3 text-right font-mono">{format(it.quantity * it.unit_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex justify-end">
              <div className="w-72 space-y-2 text-sm">
                <div className="flex justify-between text-[#5C5C5C]">
                  <span>Nëntotali</span>
                  <span className="font-mono">{format((invoice.total || 0) + (invoice.discount || 0))}</span>
                </div>
                {invoice.discount > 0 && (
                  <div className="flex justify-between text-[#5C5C5C]">
                    <span>Zbritje</span>
                    <span className="font-mono">−{format(invoice.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t-2 border-[#1A1A1A] pt-2 text-base font-semibold">
                  <span>TOTALI</span>
                  <span className="font-mono">{format(invoice.total)}</span>
                </div>
                <div className="flex justify-between text-[#5C5C5C]">
                  <span>Paguar</span>
                  <span className="font-mono">{format(invoice.paid)}</span>
                </div>
                <div className="flex justify-between font-semibold text-[#9E3C3C]">
                  <span>Mbetur</span>
                  <span className="font-mono">{format(Math.max(0, invoice.total - invoice.paid))}</span>
                </div>
              </div>
            </div>

            {invoice.notes && (
              <div className="mt-10 pt-6 border-t border-[#E2DFD8] text-sm text-[#5C5C5C]">
                <div className="label-eyebrow mb-1">Shënime</div>
                {invoice.notes}
              </div>
            )}

            <div className="mt-16 text-center text-xs text-[#5C5C5C]">
              Faleminderit për besimin! · {brand} © {new Date().getFullYear()}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          body * { visibility: hidden; }
          #invoice-print, #invoice-print * { visibility: visible; }
          #invoice-print { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
    </div>
  );
}
